import React, { useState, useEffect, useRef } from 'react';
import { Mic, Square, Loader2, Volume2, Flag } from 'lucide-react';
import {
  fetchAiConversationScenarios,
  aiConversationReply,
  aiConversationSummary,
  assessPronunciation,
  fetchAudioWithTimings,
  recordExerciseAttempt,
} from '../../services/api';
import { useWavRecorder } from '../../hooks/useWavRecorder';
import { useExerciseTimer } from '../../hooks/useExerciseTimer';
import ExerciseLayout from '../shared/ExerciseLayout';
import ExerciseCard from '../shared/ExerciseCard';
import PronunciationWords, { scoreColor } from '../shared/PronunciationWords';
import Loading from '../Loading';
import AvatarHead from './AvatarHead';

// Volná řeč je delší než čtená věta — limit je tu volnější než u Mluvení.
const MAX_RECORDING_SECONDS = 30;

const difficultyLabels = { easy: 'lehká', medium: 'střední', hard: 'těžká' };

const AiVoiceConversation = ({ topic, onBack }) => {
  const [scenario, setScenario] = useState(null);
  const [loading, setLoading] = useState(true);
  // {role, content, pronunciation?} — pronunciation only on the learner's turns.
  const [messages, setMessages] = useState([]);
  const [phase, setPhase] = useState('idle'); // idle | recording | assessing | replying
  const [error, setError] = useState(null);
  const [wrappingUp, setWrappingUp] = useState(false);
  const [finished, setFinished] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [summary, setSummary] = useState(null);

  const { start, stop } = useWavRecorder();
  const { stop: stopTimer } = useExerciseTimer();

  const [playingIndex, setPlayingIndex] = useState(null);
  // Postupné odhalování textu AI podle toho, jak audio dochází ke slovům.
  // { index, words: [{word, start}], count } — count je počet už odhalených slov.
  const [reveal, setReveal] = useState(null);
  // true, když právě hraje zvuk AI — řídí pohyb úst avatara.
  const [speaking, setSpeaking] = useState(false);
  const audioRef = useRef(null);
  const rafRef = useRef(null);
  const blobUrlRef = useRef(null); // aktuální objektové URL audia — ať jde uvolnit i při přerušení
  const lastCountRef = useRef(-1); // naposledy odhalený počet slov — překreslujeme jen při změně
  const autoStopRef = useRef(null);
  const endRef = useRef(null);
  // Web Audio: jeden AudioContext + analyzér sdílený s avatarem. Zvuk AI vedeme
  // přes analyzér, ze kterého avatar odečítá frekvence a podle nich hýbe pusou.
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const [analyserNode, setAnalyserNode] = useState(null);

  // Líně vytvoří AudioContext + analyzér (jednou). AudioContext po vzniku bývá
  // „pozastavený", probudíme ho až při přehrání (po interakci uživatele).
  const ensureAudioGraph = () => {
    if (!audioCtxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.5;
      analyser.connect(ctx.destination);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
      setAnalyserNode(analyser);
    }
    return { ctx: audioCtxRef.current, analyser: analyserRef.current };
  };

  const stopPlayback = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    lastCountRef.current = -1;
    setPlayingIndex(null);
    setReveal(null);
    setSpeaking(false); // avatar přestane hýbat pusou
  };

  // Play one AI line aloud through the existing TTS endpoint (cached server-side)
  // and reveal its words in sync with the audio, like the dialogue exercise.
  const playMessage = async (text, index) => {
    stopPlayback();
    setPlayingIndex(index);
    // Než dorazí časování, text schováme — místo něj tři tečky.
    setReveal({ index, words: [], count: 0 });
    try {
      const { audioBlob, wordTimings } = await fetchAudioWithTimings(text);
      const timings = wordTimings || [];
      // Bez časování (TTS ho nevrátil) odhalování nejde — ukážeme rovnou celý text.
      if (timings.length === 0) setReveal(null);

      const blobUrl = URL.createObjectURL(audioBlob);
      blobUrlRef.current = blobUrl;
      const audio = new Audio(blobUrl);
      audioRef.current = audio;

      // Zvuk vedeme přes Web Audio analyzér, ze kterého avatar odečítá frekvence.
      // HTMLAudioElement zachováváme kvůli currentTime pro karaoke odhalování.
      // createMediaElementSource jde na element zavolat jen jednou — pro každé
      // přehrání vzniká nový Audio, takže je to v pořádku.
      const { ctx, analyser } = ensureAudioGraph();
      if (ctx.state === 'suspended') {
        try { await ctx.resume(); } catch { /* probudí se až po interakci */ }
      }
      try {
        const srcNode = ctx.createMediaElementSource(audio);
        srcNode.connect(analyser);
      } catch (e) {
        // Když napojení selže, avatar se nehýbe, ale zvuk hraje dál normálně.
        console.warn('Web Audio se nepodařilo napojit:', e);
      }

      audio.onended = stopPlayback; // uvolní blob URL, zruší smyčku, zastaví avatara
      setSpeaking(true); // avatar začne hýbat pusou
      await audio.play();

      if (timings.length > 0) {
        // Slovo odhalíme, jakmile jeho čas startu projde aktuálním časem audia.
        // setReveal voláme jen když se počet odhalených slov změní — ne každý snímek.
        const tick = () => {
          if (!audioRef.current) return;
          const t = audioRef.current.currentTime;
          let count = 0;
          for (let i = 0; i < timings.length; i++) {
            if (t >= timings[i].start) count = i + 1;
            else break;
          }
          if (count !== lastCountRef.current) {
            lastCountRef.current = count;
            setReveal({ index, words: timings, count });
          }
          rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
      }
    } catch (err) {
      console.error('Error playing audio:', err);
      stopPlayback();
    }
  };

  // Load the teacher's scenario for this topic and open with the AI's first line.
  useEffect(() => {
    const load = async () => {
      try {
        const scenarios = await fetchAiConversationScenarios(topic);
        if (scenarios.length > 0) {
          const s = scenarios[0];
          setScenario(s);
          setMessages([{ role: 'assistant', content: s.opening_message }]);
          playMessage(s.opening_message, 0);
        }
      } catch (err) {
        console.error('Error loading scenario:', err);
        setError(err.message || 'Nepodařilo se načíst konverzaci.');
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => {
      stopPlayback();
      if (autoStopRef.current) clearTimeout(autoStopRef.current);
      if (audioCtxRef.current) {
        audioCtxRef.current.close().catch(() => {});
        audioCtxRef.current = null;
        analyserRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic]);

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages, phase]);

  // Stop recording, transcribe + score via Azure, then ask the AI to reply.
  // The learner sees their transcript immediately; the reply loads after.
  const stopAndSend = async () => {
    if (autoStopRef.current) {
      clearTimeout(autoStopRef.current);
      autoStopRef.current = null;
    }
    setPhase('assessing');

    const blob = await stop();
    if (!blob) {
      setError('Nahrávka je prázdná, zkuste to znovu.');
      setPhase('idle');
      return;
    }

    let userMessage;
    try {
      const result = await assessPronunciation(blob); // bez reference = volná řeč
      userMessage = { role: 'user', content: result.recognized_text, pronunciation: result };
    } catch (err) {
      console.error('Pronunciation assessment failed:', err);
      setError('Nerozuměli jsme vám, zkuste to prosím znovu.');
      setPhase('idle');
      return;
    }

    const history = [...messages, userMessage];
    setMessages(history);
    setPhase('replying');

    try {
      const data = await aiConversationReply(
        scenario.id,
        history.map(({ role, content }) => ({ role, content }))
      );
      setMessages([...history, { role: 'assistant', content: data.reply }]);
      setWrappingUp(data.wrapping_up);
      playMessage(data.reply, history.length);
    } catch (err) {
      console.error('AI reply failed:', err);
      // Historie zůstává — další nahrávka pošle stejný kontext znovu.
      setError(err.message || 'AI neodpověděla. Zkuste to prosím znovu.');
    } finally {
      setPhase('idle');
    }
  };

  const handleMicClick = async () => {
    setError(null);
    if (phase === 'recording') {
      await stopAndSend();
      return;
    }
    if (phase !== 'idle') return;
    stopPlayback();
    try {
      await start();
      setPhase('recording');
      autoStopRef.current = setTimeout(stopAndSend, MAX_RECORDING_SECONDS * 1000);
    } catch {
      setError('Nepodařilo se spustit nahrávání. Povolte přístup k mikrofonu.');
    }
  };

  const userTurns = messages.filter((m) => m.role === 'user');
  const scores = userTurns
    .filter((m) => m.pronunciation)
    .map((m) => m.pronunciation.accuracy);
  const averageScore = scores.length > 0
    ? Math.round(scores.reduce((sum, s) => sum + s, 0) / scores.length)
    : 0;

  // End of the conversation: fetch the Czech wrap-up and record the attempt.
  // Neither failure blocks the learner from seeing their result.
  const finishConversation = async () => {
    stopPlayback();
    setFinishing(true);
    const finalTime = stopTimer();

    const turns = userTurns.map((m) => ({
      text: m.content,
      problem_words: (m.pronunciation?.words || [])
        .filter((w) => w.accuracy < 60)
        .map((w) => ({ word: w.word, accuracy: w.accuracy })),
    }));

    try {
      const data = await aiConversationSummary(scenario.id, turns);
      setSummary(data.summary);
    } catch (err) {
      console.error('Summary failed:', err);
      setSummary(null);
    }

    try {
      await recordExerciseAttempt({
        topic: topic,
        exercise_type: 'ai_conversation',
        score: averageScore,
        max_score: 100,
        time_spent_seconds: finalTime,
        errors: null,
      });
    } catch (trackingError) {
      console.error('Failed to record exercise attempt:', trackingError);
    }

    setFinishing(false);
    setFinished(true);
  };

  if (loading) return <Loading />;

  if (!scenario) {
    return (
      <ExerciseLayout title="Konverzace s AI" color="purple" icon="🗣️" onBack={onBack}>
        <ExerciseCard>
          <p className="text-lg text-gray-700 text-center py-6">
            Pro toto téma zatím není konverzace připravena.
          </p>
          <div className="flex justify-center">
            <button
              onClick={onBack}
              className="px-6 py-3 rounded-lg font-medium bg-gray-500 hover:bg-gray-600 text-white"
            >
              Zpět na menu
            </button>
          </div>
        </ExerciseCard>
      </ExerciseLayout>
    );
  }

  if (finished) {
    return (
      <ExerciseLayout title="Konverzace s AI" color="purple" icon="🗣️" onBack={onBack}>
        <ExerciseCard>
          <h2 className="text-xl font-semibold text-gray-800 text-center mb-4">
            Konverzace dokončena
          </h2>
          <p className="text-center text-lg mb-6">
            Průměrná přesnost výslovnosti:{' '}
            <span className={`text-2xl font-bold ${scoreColor(averageScore)}`}>
              {averageScore} / 100
            </span>
          </p>

          {summary ? (
            <div className="mb-6 p-4 rounded-xl bg-purple-50 border border-purple-100">
              <h3 className="font-semibold text-gray-800 mb-2">Shrnutí od učitele AI</h3>
              <p className="text-lg text-gray-700 whitespace-pre-wrap">{summary}</p>
            </div>
          ) : (
            <p className="mb-6 text-center text-gray-500">
              Shrnutí konverzace se nepodařilo připravit, ale výsledek je uložen.
            </p>
          )}

          <div className="flex justify-center">
            <button
              onClick={onBack}
              className="px-6 py-3 rounded-lg font-medium bg-green-500 hover:bg-green-600 text-white"
            >
              Zpět na menu
            </button>
          </div>
        </ExerciseCard>
      </ExerciseLayout>
    );
  }

  return (
    <ExerciseLayout
      title="Konverzace s AI"
      subtitle={`Obtížnost: ${difficultyLabels[scenario.difficulty] || scenario.difficulty}`}
      color="purple"
      icon="🗣️"
      onBack={onBack}
    >
      <ExerciseCard>
        <p className="mb-4 text-gray-600">
          Povídejte si s AI nahlas anglicky. Mluvte v celých větách — po odmlce
          nahrávání samo skončí. Nikam nespěchejte.
        </p>

        {/* Conversation bubbles */}
        <div className="flex flex-col gap-3 mb-6">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`px-4 py-3 rounded-2xl max-w-[85%] ${
                  msg.role === 'user'
                    ? 'bg-purple-50 border border-purple-100'
                    : 'bg-gray-50 border border-gray-100'
                }`}
              >
                {msg.role === 'assistant' ? (
                  <div className="flex items-start gap-3">
                    <p className="text-lg text-gray-800">
                      {reveal && reveal.index === idx ? (
                        reveal.words.length === 0
                          ? <span className="text-gray-400">…</span>
                          : reveal.words.slice(0, reveal.count).map((w) => w.word).join(' ')
                      ) : (
                        msg.content
                      )}
                    </p>
                    <button
                      onClick={() => playMessage(msg.content, idx)}
                      disabled={playingIndex !== null && playingIndex !== idx}
                      aria-label="Přehrát znovu"
                      title="Přehrát znovu"
                      className={`mt-1 shrink-0 ${
                        playingIndex === idx
                          ? 'text-amber-500'
                          : 'text-cyan-600 hover:text-cyan-700'
                      } disabled:opacity-40`}
                    >
                      <Volume2 size={22} />
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Transcript coloured by per-word pronunciation score */}
                    <PronunciationWords
                      words={msg.pronunciation?.words}
                      className="text-lg"
                    />
                    {msg.pronunciation && (
                      <div className="mt-1 text-sm text-gray-500">
                        Výslovnost:{' '}
                        <span className={`font-bold ${scoreColor(msg.pronunciation.accuracy)}`}>
                          {Math.round(msg.pronunciation.accuracy)} / 100
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}

          {(phase === 'assessing' || phase === 'replying') && (
            <div className="flex justify-start">
              <div className="px-4 py-3 rounded-2xl bg-gray-50 border border-gray-100 text-gray-500 flex items-center gap-2">
                <Loader2 size={18} className="animate-spin" />
                {phase === 'assessing' ? 'Vyhodnocuji…' : 'AI přemýšlí…'}
              </div>
            </div>
          )}

          <div ref={endRef} />
        </div>

        {error && (
          <div className="mb-4 text-center text-red-600">{error}</div>
        )}

        {wrappingUp && (
          <p className="mb-4 text-center text-gray-500">
            Konverzace se chýlí ke konci — můžete se rozloučit a ukončit ji.
          </p>
        )}

        {/* Mluvící hlava — dole u mikrofonu, ať zůstane vidět i v delší
            konverzaci (sem se obrazovka sama odroluje). Pohyb úst řídí zvuk AI. */}
        <div className="flex justify-center mb-4">
          <AvatarHead
            analyser={analyserNode}
            speaking={speaking}
            className="rounded-2xl overflow-hidden bg-gradient-to-b from-purple-50 to-gray-50 border border-purple-100"
            style={{ width: '100%', maxWidth: 360, height: 300 }}
          />
        </div>

        {/* Controls — one big mic button, no time pressure */}
        <div className="flex flex-col items-center gap-4">
          <button
            onClick={handleMicClick}
            disabled={phase === 'assessing' || phase === 'replying'}
            className={`w-24 h-24 rounded-full flex items-center justify-center text-white shadow-lg transition-all ${
              phase === 'recording'
                ? 'bg-red-500 hover:bg-red-600 animate-pulse'
                : phase === 'idle'
                  ? 'bg-purple-500 hover:bg-purple-600'
                  : 'bg-gray-300 cursor-not-allowed'
            }`}
            aria-label={phase === 'recording' ? 'Zastavit nahrávání' : 'Začít mluvit'}
          >
            {phase === 'recording' ? <Square size={36} /> : <Mic size={36} />}
          </button>
          <p className="text-gray-600 font-medium">
            {phase === 'recording'
              ? 'Mluvte… klepnutím nahrávání ukončíte'
              : phase === 'idle'
                ? 'Klepněte a mluvte'
                : ''}
          </p>

          {userTurns.length > 0 && phase === 'idle' && (
            <button
              onClick={finishConversation}
              disabled={finishing}
              className="px-6 py-3 rounded-lg font-medium bg-green-500 hover:bg-green-600 text-white flex items-center gap-2 disabled:opacity-60"
            >
              {finishing ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Připravuji shrnutí…
                </>
              ) : (
                <>
                  <Flag size={18} />
                  Ukončit a vyhodnotit
                </>
              )}
            </button>
          )}
        </div>
      </ExerciseCard>
    </ExerciseLayout>
  );
};

export default AiVoiceConversation;
